import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
Box,
Button,
Card,
CardHeader,
CardContent,
CardActions,
Typography,
Container,
Grid,
MenuItem,
FormControl,
Select,
Alert,
CircularProgress,
Fade,
Slide,
Grow,
useMediaQuery,
useTheme,
Divider,
Chip,
IconButton,
Tooltip,
Stack,
TextField,
Paper,
InputAdornment,
InputLabel
} from '@mui/material';
import {
ViewModule, Search,
ViewList, Refresh,
School as SchoolIcon,
Category as CategoryIcon,
Visibility as VisibilityIcon,
ArrowForward as ArrowForwardIcon,
Article as ArticleIcon,
Summarize as SummarizeIcon,
MenuBook as MenuBookIcon,
CheckCircle as CheckCircleIcon,
Science as ScienceIcon
} from '@mui/icons-material';
import { APIResearchDelete, APIResearcherList, APIResearchPdfFile, APIResearchViewsPost, APIResearchViewsStore } from '../../utilities/APIS';
import { clearUserFilters, getCurrentUserId, getIdCategory, getIdFullName, getTodaysDate, STORAGE_KEYS } from '../../utilities/Functions';
import NavigationMenu from '../navigation/NavigationMenu';
import ResearchCard from './ResearchCard'; 
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import { APICategoriesList, APIResearchList, APIUniversitiesList } from '../../utilities/APIS';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../utilities/Text';
import { headers } from '../../utilities/Env';
import LoadingState from '../../components/LoadingState';

const Researches = ({ userOnly = false, userId = null }) => {
    const [researchesData, setResearchesData] = useState([]);
    const showToast = useCustomToast();
    const [areasData, setAreasData] = useState([]);
    const [universitiesData, setUniversitiesData] = useState([]);
    const [researchersData, setResearchersData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingProductId, setDeletingProductId] = useState(null);
    const [error, setError] = useState(null);
    const [selectedUniversity, setSelectedUniversity] = useState('');
    const [selectedArea, setSelectedArea] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [sortOrder, setSortOrder] = useState('newest');
    const [statusFilter, setStatusFilter] = useState('all');
    const [researchTypeFilter, setResearchTypeFilter] = useState('all');
    const [advancedFilters, setAdvancedFilters] = useState({
        hasAbstract: false,
        hasObjectives: false,
        hasMethodology: false,
        hasResults: false,
        minWordCount: '',
        maxWordCount: ''
    });
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Initialize state from sessionStorage or props
    const [isUserOnlyMode, setIsUserOnlyMode] = useState(() => {
        const stored = sessionStorage.getItem(STORAGE_KEYS.USER_ONLY);
        return stored ? JSON.parse(stored) : (userOnly || (location.state?.userOnly || false));
    });

    const [targetUserId, setTargetUserId] = useState(() => {
        const stored = sessionStorage.getItem(STORAGE_KEYS.USER_ID);
        return stored ? JSON.parse(stored) : (userId || (location.state?.userId || null));
    });

    // Update sessionStorage when state changes
    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEYS.USER_ONLY, JSON.stringify(isUserOnlyMode));
    }, [isUserOnlyMode]);

    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEYS.USER_ID, JSON.stringify(targetUserId));
    }, [targetUserId]);

    // Also handle location state updates
    useEffect(() => {
        const locationState = location.state || {};
        if (locationState.userOnly !== undefined) {
            setIsUserOnlyMode(locationState.userOnly);
        }
        if (locationState.userId !== undefined) {
            setTargetUserId(locationState.userId);
        }
    }, [location.state]);

    useEffect(() => {
        fetchData();
    }, [isUserOnlyMode, targetUserId]);

    useEffect(() => {
        // Component mount logic here
        return () => {
            // Cleanup function - runs when component unmounts
            // Only clear filters if navigating away from researches completely
            const currentPath = location.pathname;
            if (!currentPath.startsWith('/research-')) {
                clearUserFilters();
            }
        };
    }, [location.pathname]);

    useEffect(() => {
        filterAndSortData();
    }, [researchesData, searchTerm, sortOrder, statusFilter, researchTypeFilter, isUserOnlyMode, targetUserId, advancedFilters]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Fetch all required APIs in parallel
            const [resRes, reseRes, uniRes, catsRes] = await Promise.all([
                axios.get(APIResearchList, { headers }),
                axios.get(APIResearcherList, { headers }),
                axios.get(APIUniversitiesList, { headers }),
                axios.get(APICategoriesList, { headers }),
            ]);

            let researches = Array.isArray(resRes.data.data) ? resRes.data.data.reverse() : [];
      
            let researchers = Array.isArray(reseRes.data.data) ? reseRes.data.data.reverse() : [];
            setResearchersData(researchers);

            let uni = Array.isArray(uniRes.data.data) ? uniRes.data.data.reverse() : [];
            setUniversitiesData(uni);

            let cats = Array.isArray(catsRes.data.data) ? catsRes.data.data.reverse() : [];
            setAreasData(cats);

            // Filter for user's researches only if in user-only mode
            if (isUserOnlyMode && targetUserId) {
                researches = researches.filter(researche =>
                    researche.researcherName?.toString() === targetUserId.toString()
                );
            }
            setResearchesData(researches);
            setFilteredData(researches);

            showToast("Success", TEXT_DATA_LOADED, "success");
        } catch (error) {
            let errorMessage = "Failed to load data";

            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }

            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleShowAllProjects = () => {
        clearUserFilters();
        // Also update local state
        setIsUserOnlyMode(false);
        setTargetUserId(null);
    };

    const getHeaderText = () => {
        if (isUserOnlyMode) {
            return "My Researches";
        }
        return "Research Repository";
    };

    const getSubheaderText = () => {
        if (isUserOnlyMode) {
            return "Manage and view your researches";
        }
        return "Browse and explore academic research works";
    };

    const calculateWordCount = (text) => {
        if (!text) return 0;
        return text.trim().split(/\s+/).filter(word => word.length > 0).length;
    };

    const filterAndSortData = () => {
        let filtered = researchesData;

        // Filter by search term (includes new fields)
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(research =>
                research.researchTopic?.toLowerCase().includes(searchLower) ||
                getIdCategory(research.universityName, universitiesData)?.toLowerCase().includes(searchLower) ||
                getIdFullName(research.researcherName, researchersData)?.toLowerCase().includes(searchLower) ||
                research.researchType?.toLowerCase().includes(searchLower) ||
                research.researchStatus?.toLowerCase().includes(searchLower) ||
                research.abstract?.toLowerCase().includes(searchLower) ||
                research.objectives?.toLowerCase().includes(searchLower) ||
                research.methodology?.toLowerCase().includes(searchLower) ||
                research.results_findings?.toLowerCase().includes(searchLower) ||
                research.conclusions_recommendations?.toLowerCase().includes(searchLower) ||
                research.introduction?.toLowerCase().includes(searchLower) ||
                research.literature_review?.toLowerCase().includes(searchLower)
            );
        }

        // Filter by research status
        if (statusFilter !== 'all') {
            filtered = filtered.filter(research => 
                research.researchStatus?.toLowerCase() === statusFilter.toLowerCase()
            );
        }

        // Filter by research type
        if (researchTypeFilter !== 'all') {
            filtered = filtered.filter(research => 
                research.researchType?.toLowerCase() === researchTypeFilter.toLowerCase()
            );
        }

        // Advanced filters
        if (advancedFilters.hasAbstract) {
            filtered = filtered.filter(research => 
                research.abstract && research.abstract.trim().length > 0
            );
        }

        if (advancedFilters.hasObjectives) {
            filtered = filtered.filter(research => 
                research.objectives && research.objectives.trim().length > 0
            );
        }

        if (advancedFilters.hasMethodology) {
            filtered = filtered.filter(research => 
                research.methodology && research.methodology.trim().length > 0
            );
        }

        if (advancedFilters.hasResults) {
            filtered = filtered.filter(research => 
                research.results_findings && research.results_findings.trim().length > 0
            );
        }

        // Word count filters
        if (advancedFilters.minWordCount) {
            filtered = filtered.filter(research => 
                calculateWordCount(research.abstract) >= parseInt(advancedFilters.minWordCount)
            );
        }

        if (advancedFilters.maxWordCount) {
            filtered = filtered.filter(research => 
                calculateWordCount(research.abstract) <= parseInt(advancedFilters.maxWordCount)
            );
        }

        // Sort data
        filtered = [...filtered].sort((a, b) => {
            switch (sortOrder) {
                case 'newest':
                    return new Date(b.uploadDate) - new Date(a.uploadDate);
                case 'oldest':
                    return new Date(a.uploadDate) - new Date(b.uploadDate);
                case 'title-asc':
                    return (a.researchTopic || '').localeCompare(b.researchTopic || '');
                case 'title-desc':
                    return (b.researchTopic || '').localeCompare(a.researchTopic || '');
                case 'researcher-asc':
                    return (getIdFullName(a.researcherName, researchersData) || '').localeCompare(getIdFullName(b.researcherName, researchersData) || '');
                case 'researcher-desc':
                    return (getIdFullName(b.researcherName, researchersData) || '').localeCompare(getIdFullName(a.researcherName, researchersData) || '');
                case 'type-asc':
                    return (a.researchType || '').localeCompare(b.researchType || '');
                case 'type-desc':
                    return (b.researchType || '').localeCompare(a.researchType || '');
                case 'abstract-length-asc':
                    return (a.abstract?.length || 0) - (b.abstract?.length || 0);
                case 'abstract-length-desc':
                    return (b.abstract?.length || 0) - (a.abstract?.length || 0);
                default:
                    return 0;
            }
        });

        setFilteredData(filtered);
    };

    const resetFilters = () => {
        setSelectedUniversity('');
        setSelectedArea('');
        setSearchTerm('');
        setStatusFilter('all');
        setResearchTypeFilter('all');
        setSortOrder('newest');
        setAdvancedFilters({
            hasAbstract: false,
            hasObjectives: false,
            hasMethodology: false,
            hasResults: false,
            minWordCount: '',
            maxWordCount: ''
        });
        setFilteredData(researchesData);
    };

    const resetAdvancedFilters = () => {
        setAdvancedFilters({
            hasAbstract: false,
            hasObjectives: false,
            hasMethodology: false,
            hasResults: false,
            minWordCount: '',
            maxWordCount: ''
        });
    };

    // Get unique research types for filter dropdown
    const getUniqueResearchTypes = () => {
        const types = researchesData.map(research => research.researchType).filter(Boolean);
        return [...new Set(types)].sort();
    };

    // Get unique research statuses for filter dropdown
    const getUniqueResearchStatuses = () => {
        const statuses = researchesData.map(research => research.researchStatus).filter(Boolean);
        return [...new Set(statuses)].sort();
    };

    const goToResearchDetails = async (research) => {
        await postUserViewOfResearch(research);

        const detailsProps = {
            id: research.id,
            researchTopic: research.researchTopic,
            researcherName: research.researcherName,
            researchArea: research.researchArea,
            researchStatus: research.researchStatus,
            researchType: research.researchType,
            fileName: research.fileName,
            fileSize: research.fileSize,
            uploadDate: research.uploadDate,
            universityName: research.universityName,
            pdfPlainText: research.pdfPlainText,
            abstract: research.abstract,
            acronyms: research.acronyms,
            definition_of_terms: research.definition_of_terms,
            introduction: research.introduction,
            objectives: research.objectives,
            hypothesis: research.hypothesis,
            justification: research.justification,
            significance: research.significance,
            theoretical_framework: research.theoretical_framework,
            literature_review: research.literature_review,
            methodology: research.methodology,
            results_findings: research.results_findings,
            discussion_of_results: research.discussion_of_results,
            conclusions_recommendations: research.conclusions_recommendations,
            references: research.references,
            appendices: research.appendices,
        };

        navigate("/research-details", { state: detailsProps });
    };

    const editResearchDetails = async (research) => {
        const detailsProps = {
            id: research.id,
            pdfPlainText: research.pdfPlainText,
            researchTopic: research.researchTopic,
            fileName: research.fileName,
            researcherName: research.researcherName,
            researchArea: research.researchArea,
            researchStatus: research.researchStatus,
            researchType: research.researchType,
            abstract: research.abstract,
            acronyms: research.acronyms,
            definition_of_terms: research.definition_of_terms,
            introduction: research.introduction,
            objectives: research.objectives,
            hypothesis: research.hypothesis,
            justification: research.justification,
            significance: research.significance,
            theoretical_framework: research.theoretical_framework,
            literature_review: research.literature_review,
            methodology: research.methodology,
            results_findings: research.results_findings,
            discussion_of_results: research.discussion_of_results,
            conclusions_recommendations: research.conclusions_recommendations,
            references: research.references,
            appendices: research.appendices,
        };

        navigate("/research-submit", { state: detailsProps });
    };

    const postUserViewOfResearch = async (research) => {
        try {
            const researchViewer = getCurrentUserId();
            const date = getTodaysDate();

            // Validate all required fields
            if (!researchViewer) {
                return;
            }
            const payload = {
                uploadDate: research.uploadDate,
                pdFile: research.fileName,
                topic: research.researchTopic,
                researcher: parseInt(research.researcherName) || 0,
                area: parseInt(research.researchArea) || 0,
                status: research.researchStatus,
                researchViewer: parseInt(researchViewer) || 0,
                viewDate: date,
            };

            await axios.post(APIResearchViewsStore, payload, { headers });
        } catch (err) {
            console.log(err);
            setError({
                message: err.message,
                response: err.response?.data || 'Failed to record view'
            });
        }
    };

    const getUniversityName = (id) => {
        let Name = ' ';
        let Id = parseInt(id);
        universitiesData.map((item) => { if (item.id === Id) { Name = item.name; } });
        return Name;
    };

    const getAreaName = (id) => {
        let Name = ' ';
        let Id = parseInt(id);
        areasData.map((item) => { if (item.id === Id) { Name = item.name; } });
        return Name;
    };

    const getResearcherName = (id) => {
        let Name = ' ';
        let Id = parseInt(id);
        researchersData.map((item) => { if (item.id === Id) { Name = item.full_name; } });
        return Name;
    };

    const handleDelete = async (product) => {
        // Show custom confirmation dialog
        const confirmDelete = window.confirm(
            `🚨 Delete Confirmation\n\n` +
            `Topic: ${product.researchTopic}\n` +
            `Researcher: ${getIdFullName(product.researcherName, researchersData)}\n` +
            `University: ${getIdCategory(product.universityName, universitiesData)}\n\n` +
            `This action cannot be undone. Are you sure you want to delete this research?`
        );

        if (!confirmDelete) {
            showToast("Cancelled", "Record deletion was cancelled", "info");
            return;
        }

        // Set loading state for this specific product
        setDeletingProductId(product.id);

        try {
            // Create form data with product ID
            const formDataToSend = new FormData();
            formDataToSend.append('updateId', product.id);

            // Make DELETE request
            const response = await axios.post(APIResearchDelete, formDataToSend, { headers });

            if (response.data.status === "success") {
                const successMessage = response.data.message || "Record deleted successfully!";
                showToast("Success", successMessage, "success");
                // Update local state by removing the deleted product
                setResearchesData(prev => prev.filter(p => p.id !== product.id));
                setFilteredData(prev => prev.filter(p => p.id !== product.id));
                // Show success message for a moment
                setTimeout(() => {
                    // If this was the last product in user-only mode, show message
                    if (isUserOnlyMode && filteredData.length === 1) {
                        showToast("Info", "All your products have been deleted", "info");
                    }
                }, 1000);
            } else {
                throw new Error(response.data.message || "Unknown server error");
            }
        } catch (error) {
            let errorTitle = "Delete Failed";
            let errorMessage = "Failed to delete record. Please try again.";
            if (error.response) {
                const status = error.response.status;
                switch (status) {
                    case 422:
                        const errors = error.response.data.errors;
                        errorMessage = Object.values(errors).flat().join(', ') || "Validation error";
                        break;
                    case 404:
                        errorMessage = "Record not found. It may have been already deleted.";
                        break;
                    case 403:
                        errorMessage = "You don't have permission to delete this product.";
                        break;
                    case 401:
                        errorMessage = "Please log in to perform this action.";
                        break;
                    case 500:
                        errorMessage = "Server error. Please try again later.";
                        break;
                    default:
                        errorMessage = error.response.data?.message || `Server error (${status})`;
                }
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            } else if (error.code === "ECONNABORTED") {
                errorMessage = "Request timeout. Please check your connection and try again.";
            } else if (error.message) {
                errorMessage = error.message;
            }

            showToast(errorTitle, errorMessage, "error");
        } finally {
            // Clear loading state
            setDeletingProductId(null);
        }
    };

    const getActiveFilterCount = () => {
        let count = 0;
        if (searchTerm) count++;
        if (statusFilter !== 'all') count++;
        if (researchTypeFilter !== 'all') count++;
        if (advancedFilters.hasAbstract) count++;
        if (advancedFilters.hasObjectives) count++;
        if (advancedFilters.hasMethodology) count++;
        if (advancedFilters.hasResults) count++;
        if (advancedFilters.minWordCount) count++;
        if (advancedFilters.maxWordCount) count++;
        return count;
    };

    if (loading) {
        return (
            <>
                <NavigationMenu />
                <LoadingState />
            </>
        );
    }

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <NavigationMenu />

            <Box component="main" sx={{ marginTop: 8, flexGrow: 1, p: isMobile ? 1 : 3 }}>
                <Container maxWidth="lg">
                    {/* Header Section */}
                    <Slide direction="down" in={true} mountOnEnter unmountOnExit>
                        <Box sx={{
                            mb: 3,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: theme.palette.success.main,
                            color: theme.palette.primary.contrastText,
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2
                        }}>
                            <Box>
                                <Typography variant="h5" component="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    {getHeaderText()}
                                </Typography>
                                <Typography sx={{ opacity: 0.9 }}>
                                    {getSubheaderText()}
                                </Typography>
                                {isUserOnlyMode && (
                                    <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                                        Showing {researchesData.length} of your research
                                    </Typography>
                                )}
                            </Box>

                            <Button
                                component={Link}
                                to="/research-submit"
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<ArticleIcon />}
                                sx={{
                                    fontWeight: 'bold',
                                    boxShadow: 3,
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: 6
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Submit Research
                            </Button>
                        </Box>
                    </Slide>

                    {/* Search and Filter Section */}
                    {(researchesData.length > 0 || searchTerm || statusFilter !== 'all' || researchTypeFilter !== 'all' || getActiveFilterCount() > 0) && (
                        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        placeholder="Search by title, researcher, abstract, objectives, methodology..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search color="primary" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: searchTerm && (
                                                <InputAdornment position="end">
                                                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                                                        ✕
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={2}>
                                    <FormControl fullWidth>
                                        <InputLabel>Sort By</InputLabel>
                                        <Select
                                            value={sortOrder}
                                            label="Sort By"
                                            onChange={(e) => setSortOrder(e.target.value)}
                                        >
                                            <MenuItem value="newest">Newest First</MenuItem>
                                            <MenuItem value="oldest">Oldest First</MenuItem>
                                            <MenuItem value="title-asc">Title A-Z</MenuItem>
                                            <MenuItem value="title-desc">Title Z-A</MenuItem>
                                            <MenuItem value="researcher-asc">Researcher A-Z</MenuItem>
                                            <MenuItem value="researcher-desc">Researcher Z-A</MenuItem>
                                            <MenuItem value="type-asc">Type A-Z</MenuItem>
                                            <MenuItem value="type-desc">Type Z-A</MenuItem>
                                            <MenuItem value="abstract-length-asc">Abstract: Short to Long</MenuItem>
                                            <MenuItem value="abstract-length-desc">Abstract: Long to Short</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} md={2}>
                                    <FormControl fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={statusFilter}
                                            label="Status"
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            <MenuItem value="all">All Statuses</MenuItem>
                                            {getUniqueResearchStatuses().map(status => (
                                                <MenuItem key={status} value={status}>
                                                    {status}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} md={2}>
                                    <FormControl fullWidth>
                                        <InputLabel>Research Type</InputLabel>
                                        <Select
                                            value={researchTypeFilter}
                                            label="Research Type"
                                            onChange={(e) => setResearchTypeFilter(e.target.value)}
                                        >
                                            <MenuItem value="all">All Types</MenuItem>
                                            {getUniqueResearchTypes().map(type => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6} md={2}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                        startIcon={<CategoryIcon />}
                                        fullWidth
                                    >
                                        Advanced {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
                                    </Button>
                                </Grid>
                            </Grid>

                            {/* Advanced Filters Section */}
                            {showAdvancedFilters && (
                                <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                    <Typography variant="subtitle1" gutterBottom fontWeight="600">
                                        Content Filters
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Button
                                                variant={advancedFilters.hasAbstract ? "contained" : "outlined"}
                                                onClick={() => setAdvancedFilters(prev => ({ ...prev, hasAbstract: !prev.hasAbstract }))}
                                                startIcon={<SummarizeIcon />}
                                                fullWidth
                                                sx={{ justifyContent: 'flex-start' }}
                                            >
                                                Has Abstract
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Button
                                                variant={advancedFilters.hasObjectives ? "contained" : "outlined"}
                                                onClick={() => setAdvancedFilters(prev => ({ ...prev, hasObjectives: !prev.hasObjectives }))}
                                                startIcon={<CheckCircleIcon />}
                                                fullWidth
                                                sx={{ justifyContent: 'flex-start' }}
                                            >
                                                Has Objectives
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Button
                                                variant={advancedFilters.hasMethodology ? "contained" : "outlined"}
                                                onClick={() => setAdvancedFilters(prev => ({ ...prev, hasMethodology: !prev.hasMethodology }))}
                                                startIcon={<ScienceIcon />}
                                                fullWidth
                                                sx={{ justifyContent: 'flex-start' }}
                                            >
                                                Has Methodology
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Button
                                                variant={advancedFilters.hasResults ? "contained" : "outlined"}
                                                onClick={() => setAdvancedFilters(prev => ({ ...prev, hasResults: !prev.hasResults }))}
                                                startIcon={<MenuBookIcon />}
                                                fullWidth
                                                sx={{ justifyContent: 'flex-start' }}
                                            >
                                                Has Results
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Min Abstract Words"
                                                value={advancedFilters.minWordCount}
                                                onChange={(e) => setAdvancedFilters(prev => ({ ...prev, minWordCount: e.target.value }))}
                                                InputProps={{ inputProps: { min: 0 } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Max Abstract Words"
                                                value={advancedFilters.maxWordCount}
                                                onChange={(e) => setAdvancedFilters(prev => ({ ...prev, maxWordCount: e.target.value }))}
                                                InputProps={{ inputProps: { min: 0 } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={6}>
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                onClick={resetAdvancedFilters}
                                                fullWidth
                                            >
                                                Reset Advanced Filters
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* Results Count and Active Filters */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="h6" color="text.secondary">
                                    Showing {filteredData.length} of {researchesData.length} Researches
                                    {isUserOnlyMode && " (Your Researches)"}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {searchTerm && (
                                        <Chip
                                            label={`Search: "${searchTerm}"`}
                                            onDelete={() => setSearchTerm('')}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    )}
                                    {statusFilter !== 'all' && (
                                        <Chip
                                            label={`Status: ${statusFilter}`}
                                            onDelete={() => setStatusFilter('all')}
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    )}
                                    {researchTypeFilter !== 'all' && (
                                        <Chip
                                            label={`Type: ${researchTypeFilter}`}
                                            onDelete={() => setResearchTypeFilter('all')}
                                            color="info"
                                            variant="outlined"
                                        />
                                    )}
                                    {advancedFilters.hasAbstract && (
                                        <Chip
                                            label="Has Abstract"
                                            onDelete={() => setAdvancedFilters(prev => ({ ...prev, hasAbstract: false }))}
                                            color="success"
                                            variant="outlined"
                                        />
                                    )}
                                    {advancedFilters.hasObjectives && (
                                        <Chip
                                            label="Has Objectives"
                                            onDelete={() => setAdvancedFilters(prev => ({ ...prev, hasObjectives: false }))}
                                            color="success"
                                            variant="outlined"
                                        />
                                    )}
                                    {advancedFilters.hasMethodology && (
                                        <Chip
                                            label="Has Methodology"
                                            onDelete={() => setAdvancedFilters(prev => ({ ...prev, hasMethodology: false }))}
                                            color="success"
                                            variant="outlined"
                                        />
                                    )}
                                    {advancedFilters.hasResults && (
                                        <Chip
                                            label="Has Results"
                                            onDelete={() => setAdvancedFilters(prev => ({ ...prev, hasResults: false }))}
                                            color="success"
                                            variant="outlined"
                                        />
                                    )}
                                    {advancedFilters.minWordCount && (
                                        <Chip
                                            label={`Min Words: ${advancedFilters.minWordCount}`}
                                            onDelete={() => setAdvancedFilters(prev => ({ ...prev, minWordCount: '' }))}
                                            color="warning"
                                            variant="outlined"
                                        />
                                    )}
                                    {advancedFilters.maxWordCount && (
                                        <Chip
                                            label={`Max Words: ${advancedFilters.maxWordCount}`}
                                            onDelete={() => setAdvancedFilters(prev => ({ ...prev, maxWordCount: '' }))}
                                            color="warning"
                                            variant="outlined"
                                        />
                                    )}
                                    {isUserOnlyMode && (
                                        <Chip
                                            label="My Researches Only"
                                            onDelete={handleShowAllProjects}
                                            color="success"
                                            variant="outlined"
                                        />
                                    )}
                                    {(searchTerm || statusFilter !== 'all' || researchTypeFilter !== 'all' || getActiveFilterCount() > 0) && (
                                        <Chip
                                            label="Clear All"
                                            onDelete={resetFilters}
                                            color="error"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Paper>
                    )}

                    {/* Research List */}
                    {!loading && (
                        <Fade in={!loading}>
                            <Box>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Showing {filteredData.length} research {filteredData.length === 1 ? 'work' : 'works'}
                                </Typography>

                                {filteredData.length === 0 ? (
                                    <Card sx={{ p: 4, textAlign: 'center' }}>
                                        <Typography variant="h5" color="textSecondary">
                                            No research works found matching your criteria
                                        </Typography>
                                        <Button
                                            variant="text"
                                            color="primary"
                                            onClick={resetFilters}
                                            sx={{ mt: 2 }}
                                        >
                                            Clear filters
                                        </Button>
                                    </Card>
                                ) : (
                                    <Box>
                                        {filteredData.map((research, index) => (
                                            <Grid item xs={12} key={research.id}>
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: index * 0.05 }}
                                                >
                                                    <ResearchCard
                                                        research={research}
                                                        isUserOnlyMode={isUserOnlyMode}
                                                        onDelete={handleDelete}
                                                        onEditDetails={editResearchDetails}
                                                        onViewDetails={goToResearchDetails}
                                                        getResearcherName={getResearcherName}
                                                        getUniversityName={getUniversityName}
                                                        getAreaName={getAreaName}
                                                    />
                                                </motion.div>
                                            </Grid>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Fade>
                    )}
                </Container>
            </Box>
        </Box>
    );
};

export default Researches;